import React, { useEffect, useCallback, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useEhrImportStatus, ImportCategory } from "hooks/use-ehr-connection";
import { Loading } from "components/loading";
import {
  Container,
  Header,
  Title,
  Subtitle,
  ProgressSection,
  ProgressBar,
  ProgressText,
  ImportChecklist,
  ChecklistItem,
  StatusIcon,
  CategoryName,
  ItemCount,
  ContinueButton,
} from "./styles";

function getStatusIcon(status: string, required: boolean): string {
  switch (status) {
    case "completed":
      return "✓";
    case "loading":
      return "○";
    default:
      return required ? "○" : "·";
  }
}

export function EhrImportStatusPage() {
  const history = useHistory();
  const { importStatus, isLoading, refetch } = useEhrImportStatus();
  const [canProceed, setCanProceed] = useState(false);
  const lastProgressRef = useRef(0);
  const staleTimeRef = useRef(0);
  const POLLING_INTERVAL = 2000;
  const STALE_TIMEOUT = 30000; // Auto-advance if no progress for 30 seconds

  // Poll for status updates
  useEffect(() => {
    // Stop polling if fully complete
    if (importStatus?.is_complete) {
      return;
    }

    const interval = setInterval(() => {
      refetch();
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [importStatus?.is_complete, refetch]);

  // Smart timeout: track progress and allow early exit when ready
  useEffect(() => {
    if (!importStatus) return;

    // Check if required data is ready (Phase 1 complete)
    if (importStatus.ready_to_proceed && !canProceed) {
      setCanProceed(true);
    }

    // Track progress for smart timeout
    const currentProgress = importStatus.phase1_progress || importStatus.progress || 0;
    
    if (currentProgress > lastProgressRef.current) {
      // Making progress, reset stale timer
      lastProgressRef.current = currentProgress;
      staleTimeRef.current = 0;
    } else {
      // No progress, increment stale timer
      staleTimeRef.current += POLLING_INTERVAL;
    }

    // Auto-advance if:
    // 1. Phase 1 is complete (required data ready), OR
    // 2. Stuck for too long (no progress for 30 seconds)
    if (importStatus.ready_to_proceed) {
      // Short delay to show ready state, then auto-advance
      const timeout = setTimeout(() => {
        navigateToSurvey();
      }, 2000);
      return () => clearTimeout(timeout);
    }

    // Fallback: advance if stuck for too long
    if (staleTimeRef.current >= STALE_TIMEOUT) {
      navigateToSurvey();
    }
  }, [importStatus]);

  const navigateToSurvey = useCallback(() => {
    // Mark that EHR data was connected and flow is complete
    sessionStorage.setItem("ehr_connected", "true");
    sessionStorage.setItem("ehr_flow_complete", "true");
    history.push("/");
  }, [history]);

  if (isLoading) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  if (!importStatus || importStatus.status === "no_connection") {
    // No connection, redirect back
    history.push("/ehr-connect");
    return null;
  }

  // Use phase1_progress for the main progress bar until ready, then show overall
  const displayProgress = importStatus.ready_to_proceed 
    ? importStatus.progress 
    : importStatus.phase1_progress;
  const categories = importStatus.categories || [];
  
  // Separate required and optional categories
  const requiredCategories = categories.filter((c) => c.required);
  const optionalCategories = categories.filter((c) => !c.required);

  const getProgressText = () => {
    if (importStatus.is_complete) {
      return "Import complete!";
    }
    if (importStatus.ready_to_proceed) {
      return "Essential records ready! Continuing import in background...";
    }
    return importStatus.phase_label || `${displayProgress}% complete`;
  };

  const getButtonText = () => {
    if (importStatus.is_complete) {
      return "Continue to Assessment";
    }
    if (importStatus.ready_to_proceed) {
      return "Continue to Assessment";
    }
    return "Please wait...";
  };

  return (
    <Container>
      <Header>
        <Title>Importing Your Health Records</Title>
        <Subtitle>
          {importStatus.vendor_name
            ? `Importing from ${importStatus.vendor_name}...`
            : "This will only take a moment..."}
        </Subtitle>
      </Header>

      <ProgressSection>
        <ProgressBar $value={displayProgress} />
        <ProgressText>{getProgressText()}</ProgressText>
      </ProgressSection>

      {/* Required categories (Phase 1) */}
      <ImportChecklist>
        <p style={{ fontSize: 12, color: "#666", marginBottom: 8, fontWeight: 500 }}>
          Essential Records
        </p>
        {requiredCategories.map((category: ImportCategory) => (
          <ChecklistItem key={category.name} $required={true}>
            <StatusIcon $status={category.status}>
              {getStatusIcon(category.status, true)}
            </StatusIcon>
            <CategoryName>{category.name}</CategoryName>
            {category.count > 0 && <ItemCount>{category.count} items</ItemCount>}
          </ChecklistItem>
        ))}
      </ImportChecklist>

      {/* Optional categories (Phase 2) - show only if Phase 1 complete */}
      {importStatus.ready_to_proceed && optionalCategories.length > 0 && (
        <ImportChecklist style={{ marginTop: 16, opacity: 0.7 }}>
          <p style={{ fontSize: 12, color: "#999", marginBottom: 8 }}>
            Additional Records (importing in background)
          </p>
          {optionalCategories.map((category: ImportCategory) => (
            <ChecklistItem key={category.name} $required={false}>
              <StatusIcon $status={category.status}>
                {getStatusIcon(category.status, false)}
              </StatusIcon>
              <CategoryName style={{ color: "#666" }}>{category.name}</CategoryName>
              {category.count > 0 && <ItemCount>{category.count} items</ItemCount>}
            </ChecklistItem>
          ))}
        </ImportChecklist>
      )}

      <ContinueButton 
        onClick={navigateToSurvey}
        disabled={!canProceed && !importStatus.ready_to_proceed}
      >
        {getButtonText()}
      </ContinueButton>
    </Container>
  );
}

export default EhrImportStatusPage;
